/**
 * Native download utilities for Capacitor and React Native apps
 * Bypasses CORS restrictions by using native HTTP clients
 */

import { toast } from "sonner"

/**
 * Download file using Capacitor's native HTTP client
 */
async function downloadWithCapacitor(url: string, filename: string): Promise<boolean> {
  try {
    // Written against `Http.downloadFile()`, an API that existed in an older
    // @capacitor-community/http plugin and was never part of @capacitor/core.
    // Core's HTTP plugin is CapacitorHttp, which only does request/get/post/etc
    // — no file-to-disk download — so the correct v8 approach is: fetch the
    // response as base64 via CapacitorHttp, then write it with Filesystem.
    const { CapacitorHttp } = await import('@capacitor/core')
    const { Filesystem, Directory } = await import('@capacitor/filesystem')

    console.log('[native-download] Using Capacitor HTTP for:', url)

    const response = await CapacitorHttp.get({ url, responseType: 'blob' })
    // responseType 'blob' returns base64-encoded data in .data on native platforms.
    const result = await Filesystem.writeFile({
      path: filename,
      data: response.data,
      directory: Directory.Documents,
    })

    console.log('[native-download] ✅ Downloaded to:', result.uri)
    return true
  } catch (error) {
    console.error('[native-download] Capacitor download failed:', error)
    return false
  }
}

/**
 * Download file using React Native's fetch and file system
 */
async function downloadWithReactNative(url: string, filename: string): Promise<boolean> {
  try {
    console.log('[native-download] Using React Native fetch for:', url)
    
    // React Native fetch doesn't have CORS restrictions
    const response = await fetch(url)
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    
    const blob = await response.blob()
    
    // Use React Native's file system (implementation depends on your RN setup)
    // This is a placeholder - you'd need to implement based on your RN file system library
    console.log('[native-download] ✅ Downloaded blob:', blob.size, 'bytes')
    
    // TODO: Save to device storage using react-native-fs or similar
    
    return true
  } catch (error) {
    console.error('[native-download] React Native download failed:', error)
    return false
  }
}

/**
 * Download file using native platform capabilities
 * Bypasses CORS restrictions entirely
 */
export async function downloadNative(url: string, filename: string): Promise<boolean> {
  try {
    // Check if Capacitor is available
    if (typeof window !== 'undefined' && (window as any).Capacitor) {
      return await downloadWithCapacitor(url, filename)
    }
    
    // Check if React Native is available
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
      return await downloadWithReactNative(url, filename)
    }
    
    console.warn('[native-download] No native platform detected')
    return false
  } catch (error) {
    console.error('[native-download] Native download error:', error)
    return false
  }
}

/**
 * Download file with progress tracking (native only)
 */
export async function downloadNativeWithProgress(
  url: string,
  filename: string,
  onProgress?: (progress: number) => void
): Promise<boolean> {
  try {
    if (typeof window !== 'undefined' && (window as any).Capacitor) {
      try {
        // Same v8 API correction as downloadWithCapacitor above.
        const { CapacitorHttp } = await import('@capacitor/core')
        const { Filesystem, Directory } = await import('@capacitor/filesystem')

        console.log('[native-download] Starting download with progress tracking:', url)

        // CapacitorHttp has no progress events, so this can only report 0 -> 100.
        const response = await CapacitorHttp.get({ url, responseType: 'blob' })
        await Filesystem.writeFile({
          path: filename,
          data: response.data,
          directory: Directory.Documents,
        })

        onProgress?.(100)
        return true
      } catch (capacitorError) {
        console.warn('[native-download] Capacitor modules not available:', capacitorError)
        // Fall through to browser download
      }
    }
    
    // Fallback to regular native download
    return await downloadNative(url, filename)
  } catch (error) {
    console.error('[native-download] Progress download failed:', error)
    return false
  }
}
